import { ICamera } from '@api/models';
import { GoogleMap } from 'modules/shared/features/google-map';
import { FC, memo, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

/* -------------------------------------------------------------------------------------------------
 * Component: MapCameraMarker
 * -----------------------------------------------------------------------------------------------*/

interface MapProjectMarkerProps {
    camera: ICamera;
    isSelected?: boolean;
    selectedAngle?: number;
    selectedRange?: number;
    selectedFOV?: number;
    onMarkerClick?: () => void;
}

const MapCameraMarker: FC<MapProjectMarkerProps> = (props) => {
    const { camera, isSelected = false, onMarkerClick, selectedAngle, selectedFOV } = props;

    const navigate = useNavigate();

    const link = useMemo(() => {
        return `${camera._id}`;
    }, [camera._id]);

    const cameraPosition = useMemo(() => {
        return { lat: +camera.location.latitude, lng: +camera.location.longitude };
    }, [camera.location.latitude, camera.location.longitude]);

    // Use camera's stored angle for tooltip positioning
    // const cameraAngle = useMemo(() => {
    //     return +camera.angle || 45;
    // }, [camera.angle]);

    const normalized = (n: number) => ((n % 360) + 360) % 360;

    const forwardAngle = useMemo(() => {
        // Match polygon direction:
        // - if selected, use UI-selected angle (cameraAngle prop from parent)
        // - else use stored camera.angle
        const base = isSelected && selectedAngle !== undefined ? selectedAngle : +camera.angle || 45;
        return normalized(base);
    }, [isSelected, selectedAngle, camera.angle]);

    const effectiveFOV = useMemo(() => {
        // Match polygon FOV:
        const base = isSelected && selectedFOV !== undefined ? selectedFOV : +camera.fieldOfView || 90;
        return base;
    }, [isSelected, selectedFOV, camera.fieldOfView]);

    const { position: contentPosition, direction } = useMemo(() => {
        // Place tooltip away from wedge
        let tooltipAngle = normalized(forwardAngle + 360);

        // For wide FOV, "behind" may still look cramped → move to side
        if (effectiveFOV >= 150) tooltipAngle = normalized(forwardAngle + 180);

        const offsetPx = effectiveFOV >= 120 ? 16 : 8;

        let pos: React.CSSProperties;
        let dir: 'top' | 'bottom' | 'left' | 'right';

        // 0° up, 90° right, 180° down, 270° left
        if (tooltipAngle >= 315 || tooltipAngle < 45) {
            pos = { bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: `${offsetPx}px` };
            dir = 'top';
        } else if (tooltipAngle >= 45 && tooltipAngle < 135) {
            pos = { left: '100%', top: '50%', transform: 'translateY(-50%)', marginLeft: `${offsetPx}px` };
            dir = 'right';
        } else if (tooltipAngle >= 135 && tooltipAngle < 225) {
            pos = { top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: `${offsetPx}px` };
            dir = 'bottom';
        } else {
            pos = { right: '100%', top: '50%', transform: 'translateY(-50%)', marginRight: `${offsetPx}px` };
            dir = 'left';
        }

        return { position: pos, direction: dir };
    }, [forwardAngle, effectiveFOV]);

    // Tooltip position calculation
    // const { position: contentPosition, direction } = useMemo(() => {
    //     const normalizedDegree = ((cameraAngle % 360) + 360) % 360;

    //     let pos: React.CSSProperties;
    //     let dir: 'top' | 'bottom' | 'left' | 'right';

    //     if (normalizedDegree >= 315 || normalizedDegree < 45) {
    //         pos = { top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: '8px' };
    //         dir = 'bottom';
    //     } else if (normalizedDegree >= 45 && normalizedDegree < 135) {
    //         pos = { right: '100%', top: '50%', transform: 'translateY(-50%)', marginRight: '8px' };
    //         dir = 'left';
    //     } else if (normalizedDegree >= 135 && normalizedDegree < 225) {
    //         pos = { bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: '8px' };
    //         dir = 'top';
    //     } else {
    //         pos = { left: '100%', top: '50%', transform: 'translateY(-50%)', marginLeft: '8px' };
    //         dir = 'right';
    //     }

    //     return { position: pos, direction: dir };
    // }, [cameraAngle]);

    const arrowConfig = useMemo(() => {
        return {
            position: {
                bottom: { top: '-14px', left: '50%', transform: 'translateX(-50%)' },
                top: { bottom: '-14px', left: '50%', transform: 'translateX(-50%)' },
                left: { right: '-14px', top: '50%', transform: 'translateY(-50%)' },
                right: { left: '-14px', top: '50%', transform: 'translateY(-50%)' },
            }[direction],
            rotation: {
                bottom: 180,
                left: 270,
                top: 0,
                right: 90,
            }[direction],
        };
    }, [direction]);

    const renderIcon = useCallback(() => {
        if (camera.latestImage?.urlThumb)
            return (
                <img
                    className="w-10 h-10 rounded-md object-cover object-center"
                    src={camera.latestImage.urlThumb}
                    alt=""
                />
            );

        return (
            <div className="w-10 h-10 bg-grayscale-100 rounded-md flex items-center justify-center">
                <svg
                    width={'40%'}
                    viewBox="0 0 114 70"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ borderRadius: '4px' }}
                >
                    {/* ... SVG paths ... */}
                </svg>
            </div>
        );
    }, [camera.latestImage?.urlThumb]);

    // const handleMarkerClick = useCallback(() => {
    //     if (onMarkerClick) {
    //         onMarkerClick();
    //     } else {
    //         navigate(link);
    //     }
    // }, [onMarkerClick, navigate, link]);

    // Memoize the marker content to prevent unnecessary re-renders
    const markerContent = useMemo(
        () => (
            <div className="relative flex justify-center">
                {/* Tooltip */}
                <div className="absolute flex flex-col items-center drop-shadow-md" style={{ ...contentPosition }}>
                    <div
                        className={`bg-card-color fill-card-color px-2 py-2 rounded-lg text-xs transition-all ${
                            isSelected ? 'ring-2 ring-blue-500 shadow-lg' : ''
                        }`}
                    >
                        <div className="absolute" style={arrowConfig.position}>
                            <svg
                                className="fill-inherit"
                                fill="currentColor"
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                style={{
                                    transform: `rotate(${arrowConfig.rotation}deg)`,
                                    transformOrigin: 'center',
                                }}
                            >
                                <path stroke="none" d="M0,0 H14 L7,7 Q7,7 7,7 Z"></path>
                            </svg>
                        </div>

                        <div className="flex items-center gap-3">
                            <div>{renderIcon()}</div>

                            <div>
                                <div className="font-medium whitespace-nowrap">{camera.name}</div>
                                <p className="text-xs text-grayscale-500 whitespace-nowrap">{camera.location.name}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        ),
        [contentPosition, isSelected, arrowConfig, renderIcon, camera.name, camera.location.name]
    );

    return (
        <GoogleMap.AdvancedMarker
            position={cameraPosition}
            zIndex={isSelected ? 100 : 10}
            onClick={() => {
                // e.stopPropagation();
                onMarkerClick();
            }}
        >
            {markerContent}
        </GoogleMap.AdvancedMarker>
    );
};

// Memoize the component with custom comparison
export default memo(MapCameraMarker, (prev, next) => {
    const sameBase =
        prev.camera._id === next.camera._id &&
        prev.camera.name === next.camera.name &&
        prev.camera.location.latitude === next.camera.location.latitude &&
        prev.camera.location.longitude === next.camera.location.longitude &&
        prev.camera.latestImage?.urlThumb === next.camera.latestImage?.urlThumb &&
        prev.isSelected === next.isSelected;

    if (!sameBase) return false;

    // Only care about selected props if marker is selected
    if (next.isSelected) {
        return (
            prev.selectedAngle === next.selectedAngle &&
            prev.selectedFOV === next.selectedFOV &&
            prev.selectedRange === next.selectedRange
        );
    }

    return true;
});
